import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Modal, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { updateEntity } from "../../../api/entity";
import { UserInfo } from "../../../types";
import { useProfileData } from "../../../hooks/useProfileData";
import { useInvalidateMyQuery } from "../../../hooks/useInvalidateMyQuery";
import { colors } from "../../../theme/palette";
import { ClosePopupIcon } from "../../../assets/icons/MUI/EntitiesIcons";
import ImageUploader from "../../../shared/ImageUploader/ImageUploader";
import { useImageUploader } from "../../../shared/ImageUploader/useImageUploader";

const updateEntityProfile = (id: number, body: any) => {
  return updateEntity(id, body);
};

const EditProfileModal = ({
  openModal,
  entityInfo,
  closeModal,
}: {
  openModal: boolean;
  entityInfo: UserInfo;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  const updateUserData = useInvalidateMyQuery("userInfo");
  const updateEntityProfileData = useInvalidateMyQuery("EntityProfile");
  const { data: entityData, isLoading: isEntityDataLoading } = useProfileData();
  const { entityId, role, mediaFileName, fullName, about, entityLevel, countryName, firstTwoChr } = entityData;

  const [entityAbout, setEntityAbout] = React.useState<any>(about);
  const [aboutError, setAboutError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setEntityAbout(about);
  }, [about]);

  const {
    targetURL,
    uploadedIMG,
    IMGName,
    isImageUploading,
    handleUploadStart,
    handleUploadToS3,
    handleUploadComplete,
    resetImageUpload,
  } = useImageUploader();

  const handleUpdatingEntity = async () => {
    if (!entityId || !entityAbout || !uploadedIMG) return;
    setIsLoading(true);

    await handleUploadToS3();

    await updateEntityProfile(entityId, {
      about: entityAbout ? entityAbout : about,
      mediaUrl: IMGName ? IMGName : mediaFileName,
    })
      .then((res) => {
        updateUserData();
        updateEntityProfileData();
      })
      .catch((e) => {})
      .finally(() => {
        resetImageUpload();
        setIsLoading(false);
        closeModal();
      });
  };

  return (
    <Modal open={openModal} onClose={closeModal} aria-labelledby="Player Advanced Search">
      <Box
        sx={{
          width: "710px",
          height: "fit-content",
          maxHeight: "calc(100% - 100px)",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: colors.White,
          borderRadius: "12px ",
          overflow: "auto",
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: "15px",
            left: "15px",
            cursor: "pointer",
          }}
          onClick={closeModal}
        >
          <ClosePopupIcon />
        </IconButton>
        <Typography
          variant="h4ExtraBold"
          sx={{
            borderBottom: "1px solid #EAECF0",
            padding: "21px 0 18px",
            margin: "0 20px 40px",
            textAlign: "center",
          }}
        >
          {t("pages.entity.entityEditing")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            rowGap: "21px",
            maxWidth: "410px",
            margin: "0 auto",
            paddingBottom: "40px",
          }}
        >
          <ImageUploader
            imageSrc={entityData.media}
            altText={firstTwoChr}
            onUploadStart={handleUploadStart}
            onUploadComplete={handleUploadComplete}
            disabled={isImageUploading}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              width: "100%",
            }}
          >
            <Typography variant="body14SemiBold"> {t("pages.entity.footballEntityName")}</Typography>
            <TextField value={fullName} disabled />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              width: "100%",
            }}
          >
            <Typography variant="body14SemiBold"> {t("pages.entity.entityLevel")}</Typography>
            <TextField value={entityLevel} disabled />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              width: "100%",
            }}
          >
            <Typography variant="body14SemiBold"> {t("pages.entity.entityType")}</Typography>
            <TextField value={role} disabled />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              width: "100%",
            }}
          >
            <Typography variant="body14SemiBold"> {t("pages.entity.country")}</Typography>
            <TextField value={countryName} disabled />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              width: "100%",
            }}
          >
            <Typography variant="body14SemiBold"> {t("pages.entity.aboutEntity")}</Typography>
            <TextField
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "8px !important",
                  paddingRight: "0 !important",
                },
              }}
              value={entityAbout}
              multiline
              rows={4}
              error={aboutError}
              onChange={(event) => {
                if (/^\s+$|^$/.test(event.target.value)) {
                  setAboutError(true);
                } else {
                  setAboutError(false);
                }
                setEntityAbout(event.target.value);
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            {t("pages.entity.inCaseParagraph")}{" "}
            <a
              href="mailto:ask@goalngo.com"
              style={{
                color: colors.GreenDarkMode,
              }}
            >
              ask@goalngo.com
            </a>
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "17px 0 27px",
            margin: "0 34px",
            borderTop: "1px solid #EAECF0",
          }}
        >
          <Button variant="text-sm" onClick={closeModal}>
            {t("pages.entity.cancel")}
          </Button>

          <Button
            variant="primary-sm"
            disabled={isImageUploading || aboutError || entityAbout === "" || isLoading}
            onClick={handleUpdatingEntity}
          >
            {!isLoading && t("pages.entity.update")}
            {isLoading && "...."}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditProfileModal;
